// ─────────────────────────────────────────────────────────────────────────────
// CONSUMO ELÉCTRICO — CUENTAS COMPARTIDAS Y HORA DE REPÚBLICA DOMINICANA
//
// Dos cosas que usan por igual el módulo de Consumo, el Panel y los Reportes.
// Viven aquí para que las tres pantallas no puedan discrepar.
//
// ── 1. LA RESTA SE HACE POR CONTADOR ────────────────────────────────────────
// El consumo sale de restarle a cada lectura la anterior, y esa resta SOLO
// vale entre lecturas del mismo medidor. Antes se restaba por punto comercial,
// que daba igual mientras cada punto tenía un único medidor; con varios, esa
// cuenta se vuelve basura:
//
//   Oficina  lunes   290,500      \  restadas en orden por punto comercial,
//   Planta   martes    9,800      /  dan −280,700 (se recorta a 0) y luego
//   Oficina  miércoles 291,000       +281,200 de golpe. Ninguno es real.
//
// El contador se escribe a mano en cada lectura, igual que el número del
// medidor: no hay catálogo que mantener. Por eso se agrupa por su texto, y se
// normaliza (sin espacios de sobra, en mayúsculas) para que "Planta",
// "planta " y "PLANTA" sean el mismo medidor y no tres series distintas.
//
// ── 2. LA HORA ES LA DE AQUÍ, NO LA DEL EQUIPO ──────────────────────────────
// Ver más abajo: una lectura tiene que quedar con la fecha y la hora de
// República Dominicana aunque la computadora esté mal configurada.
// ─────────────────────────────────────────────────────────────────────────────

import type { Consumo } from './types';

/** Zona horaria de la empresa. RD no cambia de hora en todo el año. */
export const ZONA_RD = 'America/Santo_Domingo';

/**
 * El texto del contador, normalizado para agrupar. Lo que se guarda y se
 * imprime es lo que la persona escribió; esto es solo para comparar.
 */
export const claveContador = (texto: string | undefined) => (texto ?? '').trim().toUpperCase().replace(/\s+/g, ' ');

/** Cómo se nombra un contador cuando la lectura no trae ninguno. */
export const SIN_CONTADOR = 'Sin contador';
export const nombreContador = (texto: string | undefined) => (texto ?? '').trim() || SIN_CONTADOR;
export interface LecturaConConsumo extends Consumo {
  // kWh gastados desde la lectura anterior DEL MISMO contador. La primera
  // lectura de cada contador no tiene contra qué restar: va en 0.
  consumo: number;
}

// La clave de agrupación lleva el punto comercial además del contador: dos
// puntos distintos pueden llamarle "Oficina" a medidores distintos.
const clave = (c: Consumo) => `${c.sucursalId}|${claveContador(c.contador)}`;

/**
 * Calcula el consumo de cada lectura restándole la anterior de su contador.
 * Devuelve las mismas lecturas que entraron, en orden cronológico.
 */
export function lecturasConConsumo(consumos: Consumo[]): LecturaConConsumo[] {
  const grupos = new Map<string, Consumo[]>();
  consumos.forEach(c => {
    const k = clave(c);
    const arr = grupos.get(k);
    if (arr) arr.push(c);else grupos.set(k, [c]);
  });
  const salida: LecturaConConsumo[] = [];
  grupos.forEach(arr => {
    const asc = [...arr].sort((a, b) => a.fecha - b.fecha);
    asc.forEach((c, i) => {
      salida.push({
        ...c,
        // Se recorta en 0 a propósito: un medidor es acumulativo, así que una
        // lectura menor que la anterior es un error de tecleo, no un ahorro.
        consumo: i === 0 ? 0 : Math.max(0, c.kwh - asc[i - 1].kwh)
      });
    });
  });
  return salida.sort((a, b) => a.fecha - b.fecha);
}

/** Los contadores que aparecen en las lecturas de un punto, para sugerirlos. */
export function contadoresUsados(consumos: Consumo[], sucursalId: string): string[] {
  const vistos = new Map<string, string>();
  consumos.filter(c => c.sucursalId === sucursalId).forEach(c => {
    const nombre = (c.contador ?? '').trim();
    if (nombre && !vistos.has(claveContador(nombre))) vistos.set(claveContador(nombre), nombre);
  });
  return [...vistos.values()].sort((a, b) => a.localeCompare(b, 'es'));
}

/** La última lectura de un contador en un punto (contra la que se valida). */
export function ultimaLecturaDe(consumos: Consumo[], sucursalId: string, contador: string): Consumo | undefined {
  const k = claveContador(contador);
  const suyas = consumos.filter(c => c.sucursalId === sucursalId && claveContador(c.contador) === k).sort((a, b) => a.fecha - b.fecha);
  return suyas[suyas.length - 1];
}

/** Resumen por punto comercial, sumando lo de todos sus contadores. */
export interface ResumenPunto {
  // Consumo del último período: el último tramo de CADA contador, sumados.
  periodo: number;
  // Suma de la última lectura de cada contador.
  acumulado: number;
  // La lectura más reciente del punto, sea del contador que sea.
  fecha: number;
}
export function resumenPorPunto(consumos: Consumo[], sucursalId: string): ResumenPunto {
  const porContador = new Map<string, Consumo[]>();
  consumos.filter(c => c.sucursalId === sucursalId).forEach(c => {
    const k = claveContador(c.contador);
    const arr = porContador.get(k);
    if (arr) arr.push(c);else porContador.set(k, [c]);
  });
  let periodo = 0;
  let acumulado = 0;
  let fecha = 0;
  porContador.forEach(arr => {
    const asc = [...arr].sort((a, b) => a.fecha - b.fecha);
    const ultima = asc[asc.length - 1];
    const previa = asc[asc.length - 2];
    if (!ultima) return;
    // Con una sola lectura no hay tramo anterior: se toma la lectura entera,
    // que es como se venía mostrando.
    periodo += previa ? Math.max(0, ultima.kwh - previa.kwh) : ultima.kwh;
    acumulado += ultima.kwh;
    if (ultima.fecha > fecha) fecha = ultima.fecha;
  });
  return {
    periodo,
    acumulado,
    fecha
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// LA HORA DE REPÚBLICA DOMINICANA
//
// Una lectura tiene que quedar con la fecha y la hora de AQUÍ. Antes se tomaba
// del reloj del equipo y, peor, la fecha que traía el formulario salía de
// `toISOString()`, que da la fecha en UTC: en RD (UTC−4), de las 8 de la noche
// en adelante el formulario proponía EL DÍA SIGUIENTE. Una lectura tomada un
// lunes por la noche quedaba fechada el martes.
//
// Aquí la fecha y la hora se piden por zona horaria, así que salen bien aunque
// la computadora esté en otro país o mal configurada.
// ─────────────────────────────────────────────────────────────────────────────

const dosDigitos = (n: number) => String(n).padStart(2, '0');

/** Las partes de la hora de RD para un instante dado. */
export function partesRD(instante: Date = new Date()) {
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: ZONA_RD,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(instante);
  const v = (tipo: string) => Number(partes.find(p => p.type === tipo)?.value ?? 0);
  return {
    anio: v('year'),
    mes: v('month'),
    dia: v('day'),
    // A las 12 de la noche `hour12: false` puede dar 24 en algunos motores.
    hora: v('hour') % 24,
    minuto: v('minute'),
    segundo: v('second')
  };
}

/** Hoy en RD, como 'aaaa-mm-dd' (lo que espera un <input type="date">). */
export function hoyRD(instante: Date = new Date()): string {
  const p = partesRD(instante);
  return `${p.anio}-${dosDigitos(p.mes)}-${dosDigitos(p.dia)}`;
}

/**
 * Convierte una fecha y hora de pared de RD al instante que se guarda.
 *
 * El desfase se calcula preguntándole al propio sistema cuánto va la hora de
 * RD respecto a UTC en ese momento, en vez de escribir −4 a mano: si algún día
 * el país volviera a cambiar de hora, esto sigue dando bien.
 */
export function instanteRD(fechaStr: string, hora: number, minuto: number, segundo: number): number {
  const [anio, mes, dia] = fechaStr.split('-').map(Number);
  if (!anio || !mes || !dia) return Date.now();
  const comoSiFueraUTC = Date.UTC(anio, mes - 1, dia, hora, minuto, segundo);
  // Desfase de RD respecto a UTC (para RD son −4 horas, o sea −14.400.000 ms).
  const referencia = new Date(comoSiFueraUTC);
  const p = partesRD(referencia);
  const desfase = Date.UTC(p.anio, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo) - referencia.getTime();
  return comoSiFueraUTC - desfase;
}

/**
 * El instante que se guarda para una lectura: la fecha que eligió la persona,
 * con la hora exacta de RD en que la registró.
 */
export function selloDeLectura(fechaStr: string, ahora: Date = new Date()): number {
  const p = partesRD(ahora);
  return instanteRD(fechaStr, p.hora, p.minuto, p.segundo);
}